import { ref, computed } from 'vue'
import { languages, languageCodes } from './config'
import type { UIStrings } from '@/types'

// Derive Language type from languages config
export type Language = keyof typeof languages

// Current language (reactive)
export const currentLanguage = ref<Language>('he')

// Current page ID (reactive)
export const pageId = ref('hub')
export const isSpokePage = computed(() => pageId.value !== 'hub')

// Set language and page ID from router (called from router beforeEach guard)
export function setFromRoute(langCode: string, pageIdValue: string): void {
    if (langCode in languages) {
        currentLanguage.value = langCode as Language
    }
    pageId.value = pageIdValue
}

// UI strings for current language - dynamic lookup, no switch needed
export const t = computed(() => languages[currentLanguage.value].ui)

// Current spoke config (undefined on hub pages)
export const currentSpoke = computed(() =>
    languages[currentLanguage.value].spokes?.[pageId.value]
)

// Spoke-aware UI: merges spoke overrides onto hub strings
export const effectiveUI = computed((): UIStrings => {
    const base = languages[currentLanguage.value].ui
    const spokeConfig = languages[currentLanguage.value].spokes?.[pageId.value]
    if (!spokeConfig) return base
    return { ...base, ...spokeConfig.ui }
})

// Format/quality labels for current language - dynamic lookup
export const formatLabels = computed(() => languages[currentLanguage.value].formatLabels)
export const qualityLabels = computed(() => languages[currentLanguage.value].qualityLabels)

// RTL support - derived from language config
export const isRTL = computed(() => languages[currentLanguage.value].dir === 'rtl')
export const direction = computed(() => languages[currentLanguage.value].dir)

// Re-export for convenience
export { languages, languageCodes }
